import { useState, useEffect, useMemo } from 'react';
import { Package, RotateCcw, Info, Building2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../ui/use-toast';
import { VenueSelection, LongTermTenderVenue, PlanogramConfig, SKUAvailability } from '../../types/map';

interface PlanogramStepProps {
  venueSelections: VenueSelection[];
  venues: LongTermTenderVenue[];
  skuSelections: { machineId: string; row: number; col: number }[];
  onSkuSelectionsChange: (selections: { machineId: string; row: number; col: number }[]) => void;
  pricePerSKU: number;
  onPricePerSKUChange: (price: number) => void;
}

// Map category names to filter categories
const normalizeCategory = (category: string | undefined): string => {
  if (!category) return 'All';
  const lower = category.toLowerCase();
  if (lower.includes('energy')) return 'Energy';
  if (lower.includes('protein')) return 'Protein';
  if (lower.includes('hydration') || lower.includes('beverage')) return 'Hydration';
  if (lower.includes('beauty')) return 'Beauty';
  if (lower.includes('isotonic')) return 'Isotonic';
  if (lower.includes('snack')) return 'Snacks';
  return 'All';
};


export function PlanogramStep({
  venueSelections,
  venues,
  skuSelections,
  onSkuSelectionsChange,
  pricePerSKU,
  onPricePerSKUChange,
}: PlanogramStepProps) {
  const [planogramConfigs, setPlanogramConfigs] = useState<Record<string, PlanogramConfig>>({});
  const [availabilities, setAvailabilities] = useState<Record<string, SKUAvailability>>({});
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ row: number; col: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ row: number; col: number } | null>(null);
  const [isDeselectMode, setIsDeselectMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Get all selected machine IDs - memoized to prevent infinite loops
  const allMachineIds = useMemo(() => {
    return venueSelections.flatMap((vs) => vs.machineIds);
  }, [venueSelections]);

  // Group machines by planogramId (check both direct machines and machines in tiers)
  const machinesByPlanogram = useMemo(() => {
    const groups: Record<string, { planogramId: string | null; machines: Array<{ id: string; name: string; venueName: string }> }> = {};
    
    for (const machineId of allMachineIds) {
      let machine: any = null;
      let venue: LongTermTenderVenue | undefined = undefined;
      let planogramId: string | null = null;
      
      // First check direct machines
      venue = venues.find((v) => v.machines.some((m) => m.id === machineId));
      if (venue) {
        machine = venue.machines.find((m) => m.id === machineId);
        if (machine) {
          planogramId = machine.planogramId ?? null;
        }
      }
      
      // If not found in direct machines, check tiers
      if (!machine) {
        for (const v of venues) {
          if (v.tiers) {
            for (const tier of v.tiers) {
              const tierMachine = tier.machines.find((m) => m.id === machineId);
              if (tierMachine) {
                machine = tierMachine;
                venue = v;
                // Use tier's planogram if available, otherwise machine's planogramId
                // Note: All machines in a tier should have the same planogram
                planogramId = tierMachine.planogramId ?? null;
                break;
              }
            }
            if (machine) break;
          }
        }
      }
      
      if (machine && venue) {
        const groupKey = planogramId ?? '__null__';
        
        if (!groups[groupKey]) {
          groups[groupKey] = {
            planogramId,
            machines: [],
          };
        }
        
        groups[groupKey].machines.push({
          id: machineId,
          name: machine.name,
          venueName: venue.venueName || 'Unknown Venue',
        });
      }
    }
    
    return groups;
  }, [allMachineIds, venues]);

  // Load pricing and update parent
  useEffect(() => {
    fetch('/api/brand/reservations/pricing', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch pricing');
        return res.json();
      })
      .then((data) => {
        if (data.tenderPricePerSKUPerMonth) {
          const price = data.tenderPricePerSKUPerMonth / 100; // Convert cents to pounds
          onPricePerSKUChange(price);
        }
      })
      .catch((err) => {
        console.error('Error fetching pricing:', err);
      });
  }, [onPricePerSKUChange]);

  // Load planogram configs and availability for all machines with localStorage caching
  useEffect(() => {
    if (allMachineIds.length === 0) {
      return;
    }

    let isCancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      
      // Create cache key based on machine IDs
      const cacheKey = `planogram-data-${allMachineIds.sort().join('-')}`;
      const cacheTimestampKey = `${cacheKey}-timestamp`;
      
      // Check localStorage cache (valid for 1 hour)
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
      const oneHour = 60 * 60 * 1000;
      const isCacheValid = cachedData && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < oneHour;
      
      if (isCacheValid && cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (parsed.configs && parsed.avail) {
            // Verify we have data for all machine IDs
            const hasAllData = allMachineIds.every(id => 
              (parsed.configs[id] || parsed.avail[id])
            );
            
            if (hasAllData) {
              setPlanogramConfigs(parsed.configs || {});
              setAvailabilities(parsed.avail || {});
              setIsLoading(false);
              
              // Auto-select machines based on tier grouping
              if (selectedMachineIds.length === 0 && allMachineIds.length > 0) {
                const machinesByTier = new Map<string, string[]>();
                
                for (const machineId of allMachineIds) {
                  for (const venue of venues) {
                    if (venue.tiers) {
                      for (const tier of venue.tiers) {
                        if (tier.machines.some((m) => m.id === machineId)) {
                          if (!machinesByTier.has(tier.id)) {
                            machinesByTier.set(tier.id, []);
                          }
                          machinesByTier.get(tier.id)!.push(machineId);
                          break;
                        }
                      }
                    }
                  }
                }
                
                if (machinesByTier.size === 1) {
                  const tierMachineIds = Array.from(machinesByTier.values())[0];
                  setSelectedMachineIds(tierMachineIds);
                } else {
                  setSelectedMachineIds([allMachineIds[0]]);
                }
              }
              
              return; // Use cached data, skip API calls
            }
          }
        } catch (e) {
          console.error('Error parsing cached data:', e);
          // Continue to fetch fresh data
        }
      }

      // Fetch fresh data
      const configs: Record<string, PlanogramConfig> = {};
      const avail: Record<string, SKUAvailability> = {};

      for (const machineId of allMachineIds) {
        if (isCancelled) return;

        let machine: any = null;
        let venue: LongTermTenderVenue | undefined = undefined;
        
        // First check direct machines
        venue = venues.find((v) => v.machines.some((m) => m.id === machineId));
        if (venue) {
          machine = venue.machines.find((m) => m.id === machineId);
        }
        
        // If not found in direct machines, check tiers
        if (!machine) {
          for (const v of venues) {
            if (v.tiers) {
              for (const tier of v.tiers) {
                const tierMachine = tier.machines.find((m) => m.id === machineId);
                if (tierMachine) {
                  machine = tierMachine;
                  venue = v;
                  break;
                }
              }
              if (machine) break;
            }
          }
        }

        // Check machine's planogramConfig or tier's planogramConfig
        let planogramConfigToUse = machine?.planogramConfig;
        
        // If machine is in a tier and tier has planogramConfig, use tier's config
        if (!planogramConfigToUse && machine && venue?.tiers) {
          for (const tier of venue.tiers) {
            if (tier.machines.some((m) => m.id === machineId) && tier.planogramConfig) {
              planogramConfigToUse = tier.planogramConfig;
              break;
            }
          }
        }

        if (planogramConfigToUse) {
          try {
            const config =
              typeof planogramConfigToUse === 'string'
                ? JSON.parse(planogramConfigToUse)
                : planogramConfigToUse;
            configs[machineId] = config;
          } catch (e) {
            console.error('Error parsing planogram config:', e);
          }
        }

        try {
          const res = await fetch(`/api/brand/reservations/machines/${machineId}/sku-availability`, {
            credentials: 'include',
          });
          if (res.ok && !isCancelled) {
            const data = await res.json();
            avail[machineId] = data;
          }
        } catch (err) {
          if (!isCancelled) {
            console.error(`Error fetching availability for machine ${machineId}:`, err);
          }
        }
      }

      if (!isCancelled) {
        setPlanogramConfigs(configs);
        setAvailabilities(avail);
        
        // Cache the data
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ configs, avail }));
          localStorage.setItem(cacheTimestampKey, Date.now().toString());
        } catch (e) {
          console.error('Error caching data:', e);
        }

        // Auto-select machines based on tier grouping
        if (selectedMachineIds.length === 0 && allMachineIds.length > 0) {
          // Check if all machines come from the same tier
          const machinesByTier = new Map<string, string[]>(); // tierId -> machineIds
          
          for (const machineId of allMachineIds) {
            for (const venue of venues) {
              if (venue.tiers) {
                for (const tier of venue.tiers) {
                  if (tier.machines.some((m) => m.id === machineId)) {
                    if (!machinesByTier.has(tier.id)) {
                      machinesByTier.set(tier.id, []);
                    }
                    machinesByTier.get(tier.id)!.push(machineId);
                    break;
                  }
                }
              }
            }
          }
          
          // If all machines are from a single tier, select all of them
          if (machinesByTier.size === 1) {
            const tierMachineIds = Array.from(machinesByTier.values())[0];
            setSelectedMachineIds(tierMachineIds);
          } else {
            // Otherwise, auto-select first machine
            setSelectedMachineIds([allMachineIds[0]]);
          }
        }
        
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      isCancelled = true;
      setIsLoading(false);
    };
  }, [allMachineIds, venues, selectedMachineIds]);

  const getCategoryForPosition = (machineId: string, row: number, col: number): string | undefined => {
    const config = planogramConfigs[machineId];
    return config?.positions?.find((p) => p.row === row && p.col === col)?.category;
  };

  const toggleSku = (row: number, col: number) => {
    // Check if any selected machine has this position allocated
    const isAllocated = selectedMachineIds.some((machineId) => {
      const availability = availabilities[machineId];
      return availability?.allocations?.some(
        (alloc) => alloc.rowIndex === row && alloc.columnIndex === col && alloc.status === 'approved'
      ) || false;
    });

    if (isAllocated) {
      toast({
        title: 'Position Unavailable',
        description: 'This SKU position is already allocated on one or more selected machines',
        variant: 'destructive',
      });
      return;
    }

    // Check if position is selected for all selected machines
    const isSelectedForAll = selectedMachineIds.every((machineId) =>
      skuSelections.some((s) => s.machineId === machineId && s.row === row && s.col === col)
    );

    if (isSelectedForAll) {
      // Deselect from all selected machines
      const updated = skuSelections.filter(
        (s) => !(selectedMachineIds.includes(s.machineId) && s.row === row && s.col === col)
      );
      onSkuSelectionsChange(updated);
    } else {
      // Select for all selected machines
      const newSelections = selectedMachineIds
        .filter((machineId) => {
          // Only add if not already selected
          return !skuSelections.some(
            (s) => s.machineId === machineId && s.row === row && s.col === col
          );
        })
        .map((machineId) => ({ machineId, row, col }));
      onSkuSelectionsChange([...skuSelections, ...newSelections]);
    }
  };

  const resetSkus = () => {
    if (selectedMachineIds.length > 0) {
      const filtered = skuSelections.filter((s) => !selectedMachineIds.includes(s.machineId));
      onSkuSelectionsChange(filtered);
    }
  };

  const handleMouseDown = (row: number, col: number) => {
    if (selectedMachineIds.length === 0) return;
    // Check if starting on a selected box (for any selected machine)
    const isStartingOnSelected = selectedMachineIds.some((machineId) =>
      skuSelections.some((s) => s.machineId === machineId && s.row === row && s.col === col)
    );
    setIsDragging(true);
    setIsDeselectMode(isStartingOnSelected);
    setDragStart({ row, col });
    setDragEnd({ row, col });
  };

  const handleMouseEnter = (row: number, col: number) => {
    if (isDragging && selectedMachineIds.length > 0) {
      setDragEnd({ row, col });
    }
  };

  const handleMouseUp = (row?: number, col?: number) => {
    if (isDragging && dragStart && dragEnd && selectedMachineIds.length > 0) {
      // Check if it was a click (no movement) or a drag
      const wasClick = dragStart.row === dragEnd.row && dragStart.col === dragEnd.col;
      
      if (wasClick && row !== undefined && col !== undefined) {
        // It was just a click, toggle the SKU for all selected machines
        toggleSku(row, col);
      } else if (!wasClick) {
        // It was a drag, handle drag selection or deselection
        const minRow = Math.min(dragStart.row, dragEnd.row);
        const maxRow = Math.max(dragStart.row, dragEnd.row);
        const minCol = Math.min(dragStart.col, dragEnd.col);
        const maxCol = Math.max(dragStart.col, dragEnd.col);

        if (isDeselectMode) {
          // Deselect mode: remove all selected boxes in the drag range for all selected machines
          const updatedSelections = skuSelections.filter((s) => {
            if (!selectedMachineIds.includes(s.machineId)) return true;
            const inRange = s.row >= minRow && s.row <= maxRow && s.col >= minCol && s.col <= maxCol;
            return !inRange;
          });
          onSkuSelectionsChange(updatedSelections);
        } else {
          // Select mode: add all unselected boxes in the drag range for all selected machines
          const newSelections: { machineId: string; row: number; col: number }[] = [];

          for (const machineId of selectedMachineIds) {
            const availability = availabilities[machineId];
            
            for (let r = minRow; r <= maxRow; r++) {
              for (let c = minCol; c <= maxCol; c++) {
                const isAllocated =
                  availability?.allocations?.some(
                    (alloc) => alloc.rowIndex === r && alloc.columnIndex === c && alloc.status === 'approved'
                  ) || false;

                if (!isAllocated) {
                  const isAlreadySelected = skuSelections.some(
                    (s) => s.machineId === machineId && s.row === r && s.col === c
                  );
                  if (!isAlreadySelected) {
                    newSelections.push({ machineId, row: r, col: c });
                  }
                }
              }
            }
          }

          if (newSelections.length > 0) {
            onSkuSelectionsChange([...skuSelections, ...newSelections]);
          }
        }
      }

      setIsDragging(false);
      setIsDeselectMode(false);
      setDragStart(null);
      setDragEnd(null);
    }
  };

  // Handle mouse up outside the grid
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseUp = () => {
      if (isDragging && dragStart && dragEnd && selectedMachineIds.length > 0) {
        // Check if it was a click (no movement) or a drag
        const wasClick = dragStart.row === dragEnd.row && dragStart.col === dragEnd.col;
        
        if (!wasClick) {
          // It was a drag, handle drag selection or deselection
          const minRow = Math.min(dragStart.row, dragEnd.row);
          const maxRow = Math.max(dragStart.row, dragEnd.row);
          const minCol = Math.min(dragStart.col, dragEnd.col);
          const maxCol = Math.max(dragStart.col, dragEnd.col);

          if (isDeselectMode) {
            // Deselect mode: remove all selected boxes in the drag range for all selected machines
            const updatedSelections = skuSelections.filter((s) => {
              if (!selectedMachineIds.includes(s.machineId)) return true;
              const inRange = s.row >= minRow && s.row <= maxRow && s.col >= minCol && s.col <= maxCol;
              return !inRange;
            });
            onSkuSelectionsChange(updatedSelections);
          } else {
            // Select mode: add all unselected boxes in the drag range for all selected machines
            const newSelections: { machineId: string; row: number; col: number }[] = [];

            for (const machineId of selectedMachineIds) {
              const availability = availabilities[machineId];
              
              for (let r = minRow; r <= maxRow; r++) {
                for (let c = minCol; c <= maxCol; c++) {
                  const isAllocated =
                    availability?.allocations?.some(
                      (alloc) => alloc.rowIndex === r && alloc.columnIndex === c && alloc.status === 'approved'
                    ) || false;

                  if (!isAllocated) {
                    const isAlreadySelected = skuSelections.some(
                      (s) => s.machineId === machineId && s.row === r && s.col === c
                    );
                    if (!isAlreadySelected) {
                      newSelections.push({ machineId, row: r, col: c });
                    }
                  }
                }
              }
            }

            if (newSelections.length > 0) {
              onSkuSelectionsChange([...skuSelections, ...newSelections]);
            }
          }
        }

        setIsDragging(false);
        setIsDeselectMode(false);
        setDragStart(null);
        setDragEnd(null);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, dragEnd, isDeselectMode, selectedMachineIds, skuSelections, availabilities, onSkuSelectionsChange]);

  const getCategoryColor = (category: string | undefined): string => {
    if (!category) return 'bg-gray-100';
    const colors: Record<string, string> = {
      'Energy Drinks': 'bg-yellow-100',
      Energy: 'bg-yellow-100',
      Snacks: 'bg-orange-100',
      Beverages: 'bg-blue-100',
      Hydration: 'bg-blue-100',
      'Healthy Options': 'bg-green-100',
      Confectionery: 'bg-pink-100',
      Beauty: 'bg-pink-100',
      Protein: 'bg-purple-100',
      Isotonic: 'bg-green-100',
    };
    return colors[category] || 'bg-purple-100';
  };

  // Use first selected machine's config for display (all selected machines should have same planogram)
  const currentConfig = selectedMachineIds.length > 0 ? planogramConfigs[selectedMachineIds[0]] : null;
  const currentAvailability = selectedMachineIds.length > 0 ? availabilities[selectedMachineIds[0]] : null;
  const selectedCountForMachine = selectedMachineIds.reduce((sum, machineId) => {
    return sum + skuSelections.filter((s) => s.machineId === machineId).length;
  }, 0);

  // Total SKUs across all machines (for pricing calculation)
  const totalSKUsAcrossAllMachines = skuSelections.length;

  // Unique number of SKU positions selected (row, col combinations) - for display only
  const uniqueSKUPositions = useMemo(() => {
    const uniquePositions = new Set(
      skuSelections.map(s => `${s.row}-${s.col}`)
    );
    return uniquePositions.size;
  }, [skuSelections]);

  // Count of machines that actually have SKUs selected
  const machinesWithSKUs = useMemo(() => {
    const uniqueMachineIds = new Set(skuSelections.map(s => s.machineId));
    return uniqueMachineIds.size;
  }, [skuSelections]);

  // Get machine name (check both direct machines and machines in tiers)
  const getMachineName = (machineId: string): string => {
    for (const venue of venues) {
      // Check direct machines
      const machine = venue.machines.find((m) => m.id === machineId);
      if (machine) return machine.name;
      
      // Check machines in tiers
      if (venue.tiers) {
        for (const tier of venue.tiers) {
          const tierMachine = tier.machines.find((m) => m.id === machineId);
          if (tierMachine) return tierMachine.name;
        }
      }
    }
    return 'Unknown Machine';
  };

  // Get venue name for machine (check both direct machines and machines in tiers)
  const getVenueName = (machineId: string): string => {
    for (const venue of venues) {
      // Check direct machines
      if (venue.machines.some((m) => m.id === machineId)) {
        return venue.venueName;
      }
      
      // Check machines in tiers
      if (venue.tiers) {
        for (const tier of venue.tiers) {
          if (tier.machines.some((m) => m.id === machineId)) {
            return venue.venueName;
          }
        }
      }
    }
    return 'Unknown Venue';
  };

  // Get planogramId for a machine (check both direct machines and machines in tiers)
  const getPlanogramId = (machineId: string): string | null => {
    for (const venue of venues) {
      // Check direct machines
      const machine = venue.machines.find((m) => m.id === machineId);
      if (machine) {
        return machine.planogramId ?? null;
      }
      
      // Check machines in tiers - use tier's planogram if available
      if (venue.tiers) {
        for (const tier of venue.tiers) {
          const tierMachine = tier.machines.find((m) => m.id === machineId);
          if (tierMachine) {
            // Use tier's planogram config if available, otherwise machine's planogramId
            if (tier.planogramConfig) {
              // Extract planogramId from tier if it has one
              // For now, return machine's planogramId, but tier should have planogramId
              return tierMachine.planogramId ?? null;
            }
            return tierMachine.planogramId ?? null;
          }
        }
      }
    }
    return null;
  };

  // Handle checkbox toggle for machine selection
  const handleMachineToggle = (machineId: string, checked: boolean) => {
    if (checked) {
      // Check if this machine can be selected with currently selected machines
      const newMachinePlanogramId = getPlanogramId(machineId);
      
      if (selectedMachineIds.length > 0) {
        // Check if all selected machines have the same planogramId
        const firstSelectedPlanogramId = getPlanogramId(selectedMachineIds[0]);
        
        if (firstSelectedPlanogramId !== newMachinePlanogramId) {
          toast({
            title: 'Cannot Select Machine',
            description: 'You can only select machines with the same planogram. Please deselect current machines first.',
            variant: 'destructive',
          });
          return;
        }
      }
      
      // Add machine to selection
      setSelectedMachineIds([...selectedMachineIds, machineId]);
    } else {
      // Remove machine from selection
      setSelectedMachineIds(selectedMachineIds.filter((id) => id !== machineId));
    }
  };

  // Check if a machine can be selected (same planogram as selected machines)
  const canSelectMachine = (machineId: string): boolean => {
    if (selectedMachineIds.length === 0) return true;
    
    const machinePlanogramId = getPlanogramId(machineId);
    const firstSelectedPlanogramId = getPlanogramId(selectedMachineIds[0]);
    
    return machinePlanogramId === firstSelectedPlanogramId;
  };

  // Filter positions by category for display
  const shouldShowPosition = (category: string | undefined): boolean => {
    if (activeFilter === 'All') return true;
    return normalizeCategory(category) === activeFilter;
  };

  // Get category counts
  const categoryCounts = useMemo(() => {
    if (!currentAvailability?.byCategory) return {};
    const counts: Record<string, { available: number; total: number }> = {};
    Object.entries(currentAvailability.byCategory).forEach(([cat, data]: [string, any]) => {
      const normalized = normalizeCategory(cat);
      if (normalized !== 'All') {
        counts[normalized] = {
          available: data.available || 0,
          total: data.total || 0,
        };
      }
    });
    return counts;
  }, [currentAvailability]);

  // Get available categories from planogram data (always include "All" first)
  const availableCategories = useMemo(() => {
    const categories = Object.keys(categoryCounts).sort();
    return ['All', ...categories];
  }, [categoryCounts]);

  // Reset filter if current filter is not available in the planogram
  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(activeFilter)) {
      setActiveFilter('All');
    }
  }, [availableCategories, activeFilter]);

  const totalCapacity = currentAvailability?.totalCapacity || 0;
  const totalAvailable = currentAvailability?.totalAvailable || 0;

  if (venueSelections.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Package className="w-12 h-12 mx-auto mb-4 text-slate-400" />
        <p className="text-lg font-medium text-slate-900 mb-1">No venues selected</p>
        <p className="text-sm text-slate-500">Please go back to Step 1 to select venues and machines</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF] mb-4"></div>
          <p className="text-lg font-medium text-slate-900">Loading planogram data...</p>
          <p className="text-sm text-slate-500 mt-2">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[calc(100vw-2rem)] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-[#0047FF]" />
            <h1 className="text-3xl font-bold text-slate-900">
              {selectedMachineIds.length > 0 ? (
                <>
                  {(() => {
                    // Check if all selected machines come from a single tier
                    const machinesByTier = new Map<string, { tierName: string; venueName: string; machineIds: string[] }>();
                    
                    for (const machineId of selectedMachineIds) {
                      for (const venue of venues) {
                        if (venue.tiers) {
                          for (const tier of venue.tiers) {
                            if (tier.machines.some((m) => m.id === machineId)) {
                              if (!machinesByTier.has(tier.id)) {
                                machinesByTier.set(tier.id, {
                                  tierName: tier.name,
                                  venueName: venue.venueName,
                                  machineIds: [],
                                });
                              }
                              machinesByTier.get(tier.id)!.machineIds.push(machineId);
                              break;
                            }
                          }
                        }
                      }
                    }
                    
                    // If all machines are from a single tier, show tier info
                    if (machinesByTier.size === 1) {
                      const tierInfo = Array.from(machinesByTier.values())[0];
                      if (tierInfo.machineIds.length === selectedMachineIds.length) {
                        return `${tierInfo.tierName} - ${tierInfo.machineIds.length} Machine${tierInfo.machineIds.length !== 1 ? 's' : ''}`;
                      }
                    }
                    
                    // Otherwise show normal machine count
                    if (selectedMachineIds.length === 1) {
                      return `${getVenueName(selectedMachineIds[0])} - ${getMachineName(selectedMachineIds[0])}`;
                    } else {
                      return `${selectedMachineIds.length} Machines Selected`;
                    }
                  })()}
                </>
              ) : (
                'Planogram & SKU Selection'
              )}
            </h1>
          </div>
        </div>

        {/* Pricing Bar */}
        {pricePerSKU > 0 && (
          <div className="mb-6 flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-900">
              £{pricePerSKU.toFixed(2)} per SKU / per machine / per month
            </span>
            <Info className="w-4 h-4 text-slate-400" />
          </div>
        )}

        {/* Machine Selector - Only show if machines are NOT all from the same tier */}
        {(() => {
          // Check if all selected machines come from a single tier
          const machinesByTier = new Map<string, string[]>(); // tierId -> machineIds
          
          for (const machineId of allMachineIds) {
            for (const venue of venues) {
              if (venue.tiers) {
                for (const tier of venue.tiers) {
                  if (tier.machines.some((m) => m.id === machineId)) {
                    if (!machinesByTier.has(tier.id)) {
                      machinesByTier.set(tier.id, []);
                    }
                    machinesByTier.get(tier.id)!.push(machineId);
                    break;
                  }
                }
              }
            }
          }
          
          // If all machines are from a single tier, don't show machine selector
          const allFromSameTier = machinesByTier.size === 1 && machinesByTier.get(Array.from(machinesByTier.keys())[0])!.length === allMachineIds.length;
          
          // Only show machine selector if there are multiple machines AND they're not all from the same tier
          if (allMachineIds.length > 1 && !allFromSameTier) {
            return (
              <div className="mb-6 space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Select Machines (same planogram only)</h3>
                {Object.entries(machinesByPlanogram).map(([groupKey, group]) => (
                  <div key={groupKey} className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="text-sm font-medium text-slate-700 mb-3">
                      {group.planogramId ? `Planogram: ${group.planogramId.substring(0, 8)}...` : 'No Planogram'}
                    </div>
                    <div className="space-y-2">
                      {group.machines.map((machine) => {
                        const isChecked = selectedMachineIds.includes(machine.id);
                        const canSelect = canSelectMachine(machine.id);
                        const skuCount = skuSelections.filter((s) => s.machineId === machine.id).length;
                        
                        return (
                          <div
                            key={machine.id}
                            className={`flex items-center space-x-3 p-2 rounded ${
                              !canSelect && selectedMachineIds.length > 0 ? 'opacity-50' : ''
                            }`}
                          >
                            <Checkbox
                              id={`machine-${machine.id}`}
                              checked={isChecked}
                              onCheckedChange={(checked) => handleMachineToggle(machine.id, checked === true)}
                              disabled={!canSelect && selectedMachineIds.length > 0}
                            />
                            <label
                              htmlFor={`machine-${machine.id}`}
                              className={`flex-1 cursor-pointer ${
                                !canSelect && selectedMachineIds.length > 0 ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <span className="text-slate-900 font-medium">
                                {machine.venueName} - {machine.name}
                              </span>
                              <span className="text-slate-500 text-sm ml-2">
                                ({skuCount} SKUs selected)
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })()}

        {currentConfig && selectedMachineIds.length > 0 && (
          <div className="space-y-6">
            {/* Top Section - Filters and Planogram */}
            <div className="space-y-6">
              {/* Category Pills */}
              {Object.keys(categoryCounts).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(categoryCounts).map(([cat, counts]) => (
                    <div
                      key={cat}
                      className="px-4 py-2 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm"
                    >
                      {cat}: {counts.available}/{counts.total}
                    </div>
                  ))}
                </div>
              )}

              {/* Filter Bar */}
              {availableCategories.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {availableCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveFilter(cat)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                        activeFilter === cat
                          ? 'bg-[#0047FF] text-white shadow-md'
                          : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-[#0047FF] h-2.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${totalCapacity > 0 ? (selectedCountForMachine / totalCapacity) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>

              {/* Planogram Grid - Full Width */}
              <div className="border rounded-lg p-4 lg:p-6 bg-white shadow-sm w-full overflow-x-auto">
                <div className="flex flex-col items-center justify-center gap-2 mx-auto w-fit">
                  {currentConfig &&
                    Array.from({ length: currentConfig.rows }, (_, row) => (
                      <div key={row} className="flex gap-2">
                        {Array.from({ length: currentConfig.columns }, (_, col) => {
                          // Use first selected machine for category (all should have same planogram)
                          const category = selectedMachineIds.length > 0 
                            ? getCategoryForPosition(selectedMachineIds[0], row, col)
                            : undefined;
                          if (!shouldShowPosition(category)) return null;

                          // Check if selected for ALL selected machines
                          const isSelected = selectedMachineIds.length > 0 && selectedMachineIds.every((machineId) =>
                            skuSelections.some((s) => s.machineId === machineId && s.row === row && s.col === col)
                          );
                          // Check if allocated on ANY selected machine
                          const isAllocated = selectedMachineIds.some((machineId) => {
                            const availability = availabilities[machineId];
                            return availability?.allocations?.some(
                              (alloc) =>
                                alloc.rowIndex === row &&
                                alloc.columnIndex === col &&
                                alloc.status === 'approved'
                            ) || false;
                          });

                          const bgColor = getCategoryColor(category);

                          // Check if this cell is in the drag selection
                          const isInDragSelection =
                            isDragging &&
                            dragStart &&
                            dragEnd &&
                            row >= Math.min(dragStart.row, dragEnd.row) &&
                            row <= Math.max(dragStart.row, dragEnd.row) &&
                            col >= Math.min(dragStart.col, dragEnd.col) &&
                            col <= Math.max(dragStart.col, dragEnd.col);

                          return (
                            <button
                              key={col}
                              type="button"
                              onMouseDown={() => handleMouseDown(row, col)}
                              onMouseEnter={() => handleMouseEnter(row, col)}
                              onMouseUp={() => handleMouseUp(row, col)}
                              disabled={isAllocated}
                              className={`
                                w-20 h-20 rounded border-2 text-xs font-medium flex flex-col items-center justify-center
                                transition-all select-none
                                ${
                                  isAllocated
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-400'
                                    : isSelected || isInDragSelection
                                    ? 'bg-[#0047FF] text-white border-[#0047FF] shadow-lg'
                                    : `${bgColor} border-gray-300 hover:border-[#0047FF] hover:shadow`
                                }
                                ${isDragging ? 'cursor-crosshair' : ''}
                              `}
                            >
                                {category && (
                                  <div className={`text-xs font-semibold text-center px-1 ${
                                    isSelected || isInDragSelection ? 'text-white' : ''
                                  }`}>
                                    {category.substring(0, 10)}
                                  </div>
                                )}
                                <div className={`text-[10px] mt-1 text-center px-1 ${
                                  isSelected || isInDragSelection ? 'text-white' : 'text-slate-600'
                                }`}>
                                  1 SKU
                                </div>
                              {isAllocated && (
                                <div className="text-[10px] mt-1 text-red-600 font-medium">Taken</div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Bottom Section - Pricing Summary and Selected SKUs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Pricing Summary */}
              <Card className="border-slate-200 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg">Pricing Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">SKUs Selected</span>
                      <span className="font-medium">{uniqueSKUPositions}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Machines</span>
                      <span className="font-medium">{machinesWithSKUs}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Price per SKU</span>
                      <span className="font-medium">£{pricePerSKU.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between font-semibold">
                        <span>Monthly Total</span>
                        <span className="text-[#0047FF]">
                          £{(pricePerSKU * totalSKUsAcrossAllMachines).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Selected SKUs - Always Visible */}
              <Card className="border-slate-200 shadow-md lg:col-span-2 flex flex-col">
                <CardHeader className="flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Selected SKUs</CardTitle>
                    {selectedCountForMachine > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={resetSkus}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <RotateCcw className="w-4 h-4 mr-1" />
                        Reset
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden flex flex-col">
                  {selectedCountForMachine === 0 ? (
                    <div className="text-sm text-slate-500 py-4 text-center">
                      No SKUs selected
                    </div>
                  ) : (
                    <div className="space-y-2 overflow-y-auto max-h-[400px] pr-2">
                      {skuSelections
                        .filter((s) => selectedMachineIds.includes(s.machineId))
                        .map((selection, idx) => {
                          const category = getCategoryForPosition(
                            selection.machineId,
                            selection.row,
                            selection.col
                          );
                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"
                            >
                              <div>
                                {category ? (
                                  <div className="font-medium text-slate-900">
                                    {category}, 1 SKU
                                  </div>
                                ) : (
                                  <div className="font-medium text-slate-900">
                                    1 SKU
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleSku(selection.row, selection.col)}
                                className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </CardContent>
              </Card>

            </div>
          </div>
        )}

        {!currentConfig && selectedMachineIds.length > 0 && (
          <div className="text-center py-8 text-slate-500">
            <p>No planogram configuration available for this machine.</p>
          </div>
        )}
      </div>

      {/* Review Configuration Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Review Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Machine Info */}
            <div>
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0047FF]" />
                Machine
              </h3>
              <p className="text-slate-700">
                {selectedMachineIds.length > 0 && (
                  <>
                    {selectedMachineIds.length === 1 ? (
                      <>
                        {getVenueName(selectedMachineIds[0])} - {getMachineName(selectedMachineIds[0])}
                      </>
                    ) : (
                      `${selectedMachineIds.length} Machines Selected`
                    )}
                  </>
                )}
              </p>
            </div>

            {/* Selected SKUs */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Selected SKUs ({selectedCountForMachine})</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {skuSelections
                  .filter((s) => selectedMachineIds.includes(s.machineId))
                  .map((selection, idx) => {
                    const category = getCategoryForPosition(
                      selection.machineId,
                      selection.row,
                      selection.col
                    );
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div>
                          <div className="font-medium text-slate-900">
                            1 SKU selected
                          </div>
                          {category && <div className="text-sm text-slate-500">{category}</div>}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg mb-3">Pricing Summary</h3>
              <div className="space-y-2 bg-slate-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">SKUs Selected</span>
                  <span className="font-medium">{uniqueSKUPositions}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Machines</span>
                  <span className="font-medium">{machinesWithSKUs}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Price per SKU</span>
                  <span className="font-medium">£{pricePerSKU.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Monthly Total</span>
                    <span className="text-[#0047FF]">
                      £{(pricePerSKU * totalSKUsAcrossAllMachines).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowReviewModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowReviewModal(false);
                // This will be handled by the parent component's submit handler
                toast({
                  title: 'Configuration Ready',
                  description: 'You can proceed to the next step to submit your tender.',
                });
              }}
              className="bg-[#0047FF] hover:bg-[#0039CC]"
            >
              Continue to Next Step
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
